import React from 'react'
import type { ComponentType, SVGProps } from 'react'
import { useLocation } from 'react-router-dom'
import { FontIcon } from '@arvoreeducacao/bonsai-ds'
import type { IconsType } from '@arvoreeducacao/bonsai-core'
import { cn } from 'utils/cn'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import {
  BOOK_DETAILS_PATH_ROOT,
  EXPLORE_PATH,
  FOREST_PATH,
  FOREST_TREES_PATH,
  LEAGUE_PATH,
  SEARCH_PATH_ROOT,
  STORY_TREE_PATH
} from 'routes/paths'

import {
  BookOpenFilledIcon,
  BookOpenIcon,
  LeafFilledIcon,
  LeafIcon
} from './icons'

type SvgIcon = ComponentType<SVGProps<SVGSVGElement>>

type NavItem = {
  label: string
  href: string
  matchPaths: string[]
  iconName: IconsType
  iconNameFilled: IconsType
  customIcon?: SvgIcon
  customIconFilled?: SvgIcon
}

const APP_V2_ORIGIN = ''

const HOME_ITEM: NavItem = {
  label: 'Home',
  href: `${APP_V2_ORIGIN}/app-v2/story-tree`,
  matchPaths: [STORY_TREE_PATH],
  iconName: 'home',
  iconNameFilled: 'home-filled'
}

const JOURNEYS_ITEM: NavItem = {
  label: 'Journeys',
  href: `${APP_V2_ORIGIN}/app-v2/journeys`,
  matchPaths: [],
  iconName: 'leaf-outline',
  iconNameFilled: 'leaf',
  customIcon: LeafIcon,
  customIconFilled: LeafFilledIcon
}

const LIBRARY_ITEM: NavItem = {
  label: 'Library',
  href: `${APP_V2_ORIGIN}/app-v2/story-tree/library`,
  matchPaths: [SEARCH_PATH_ROOT, EXPLORE_PATH, BOOK_DETAILS_PATH_ROOT],
  iconName: 'book-open',
  iconNameFilled: 'book-open',
  customIconFilled: BookOpenFilledIcon,
  customIcon: BookOpenIcon
}

const REWARDS_ITEM: NavItem = {
  label: 'Rewards',
  href: `${APP_V2_ORIGIN}/biblioteca/floresta`,
  matchPaths: [FOREST_PATH, FOREST_TREES_PATH, LEAGUE_PATH],
  iconName: 'medal',
  iconNameFilled: 'medal-filled'
}

const MY_READINGS_ITEM: NavItem = {
  label: 'My Readings',
  href: `${APP_V2_ORIGIN}/app-v2/my-readings`,
  matchPaths: ['/biblioteca/minhas-leituras'],
  iconName: 'book-person',
  iconNameFilled: 'book-person-filled'
}

const ASSIGNMENTS_ITEM: NavItem = {
  label: 'Assignments',
  href: `${APP_V2_ORIGIN}/app-v2/activities`,
  matchPaths: [],
  iconName: 'clipboard-content',
  iconNameFilled: 'clipboard-content-filled'
}

const EDUCATOR_DASHBOARD_ITEM: NavItem = {
  label: 'Educator Dashboard',
  href: `${APP_V2_ORIGIN}/app-v2/admin/class-journey-report`,
  matchPaths: [],
  iconName: 'graduation-cap',
  iconNameFilled: 'graduation-cap-filled'
}

const STUDENT_NAV_ITEMS: NavItem[] = [
  HOME_ITEM,
  JOURNEYS_ITEM,
  LIBRARY_ITEM,
  REWARDS_ITEM,
  MY_READINGS_ITEM,
  ASSIGNMENTS_ITEM
]

const TEACHER_NAV_ITEMS: NavItem[] = [
  EDUCATOR_DASHBOARD_ITEM,
  LIBRARY_ITEM,
  MY_READINGS_ITEM
]

const renderIcon = (
  item: NavItem,
  variant: 'outline' | 'filled',
  className: string
) => {
  const CustomIcon =
    variant === 'filled' ? item.customIconFilled : item.customIcon

  if (CustomIcon) {
    return <CustomIcon className={className} />
  }

  const iconName = variant === 'filled' ? item.iconNameFilled : item.iconName

  return (
    <span className={cn('inline-flex items-center justify-center', className)}>
      <FontIcon iconName={iconName} size={24} />
    </span>
  )
}

export function StoryTreeNav() {
  const location = useLocation()
  const { user } = useUserInfo()

  const isTeacher = Boolean(user?.teacher?.id)
  const navItems = isTeacher ? TEACHER_NAV_ITEMS : STUDENT_NAV_ITEMS

  const isActive = (matchPaths: string[]) => {
    const path = location.pathname
    return matchPaths.some(
      match => path === match || path.startsWith(`${match}/`)
    )
  }

  return (
    <nav
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 bg-white shadow-[0px_-24px_19px_rgba(5,59,75,0.06)]',
        isTeacher ? 'sm:hidden' : 'xl:hidden'
      )}
    >
      <div className="h-px bg-gray-100" />
      <div className="flex h-14 items-center justify-between gap-6 px-3 sm:h-[70px] sm:justify-around sm:gap-0 sm:px-6">
        {navItems.map(item => {
          const active = isActive(item.matchPaths)
          return (
            <a
              className="relative flex flex-1 flex-col items-center justify-center"
              href={item.href}
              key={item.label}
            >
              {active && (
                <div className="absolute -top-[17px] h-[3px] w-[50px] rounded-b bg-[#5CDDCF] sm:hidden" />
              )}

              <div
                className={cn(
                  'flex flex-col items-center justify-center gap-1 transition-colors sm:rounded-full sm:px-4 sm:py-1.5',
                  active
                    ? 'sm:bg-[#5CDDCF] sm:shadow-[0px_6px_4px_rgba(5,59,75,0.06)]'
                    : ''
                )}
              >
                {renderIcon(
                  item,
                  'outline',
                  cn(
                    'size-6 transition-colors sm:hidden',
                    active ? 'hidden' : 'text-gray-600'
                  )
                )}
                {renderIcon(
                  item,
                  'filled',
                  cn(
                    'size-6 transition-colors',
                    active
                      ? 'text-[#5CDDCF] sm:text-white max-sm:block'
                      : 'hidden sm:block text-[#5CDDCF]'
                  )
                )}
                <span
                  className={cn(
                    'hidden text-[10px] font-semibold sm:block',
                    active ? 'text-white' : 'text-gray-700'
                  )}
                >
                  {item.label}
                </span>
              </div>
            </a>
          )
        })}
      </div>
    </nav>
  )
}

export default StoryTreeNav
